import type { RefObject } from 'react';
import { msg } from '@gonfalon/messages/msg';

import type { FlattenedToken, ParsedToken } from '../../types/QueryBuilder';

import { useQueryBuilderContext } from './context';
import { useKeyboardSelectionContext } from './hooks/useKeyboardSelection';
import { getSelectedParsedTokens } from './utils';

import styles from './SelectionKeyHandler.module.css';

interface SelectionKeyHandlerProps {
  /** Ref to the FilterBar grid container — used to guard against clearing selection on transient React Aria focus moves. */
  gridRef: RefObject<HTMLElement | null>;
  /** Ref assigned to the hidden input element so FilterBar can focus it programmatically. */
  inputRef: RefObject<HTMLInputElement | null>;
}

/**
 * A visually-hidden focusable input that receives keyboard events while a selection
 * is active.  FilterBar moves DOM focus here whenever `selectedKeys` becomes non-empty,
 * allowing all subsequent keystrokes to be handled centrally without interfering with
 * the individual token row focus management.
 */
export function SelectionKeyHandler({ gridRef, inputRef }: SelectionKeyHandlerProps) {
  const ctx = useKeyboardSelectionContext();
  const { dispatch, formatClipboard, parseClipboard } = useQueryBuilderContext();

  // Both hooks must be called unconditionally (Rules of Hooks).
  if (!ctx) {
    return null;
  }

  const { state, allKeys, flatTokens, selectInDirection, cursorKeyRef, isFreeTextKey } = ctx;

  function getSelectedKeys(): Set<string> {
    return new Set(state.selectedKeys);
  }

  function clearSelection() {
    state.setSelectedKeys(new Set());
    cursorKeyRef.current = null;
  }

  /**
   * Moves virtual focus (and, if a FreeText container is found, DOM focus) to
   * the nearest FREE_TEXT token in the given direction relative to the selection.
   * Returns the target key so callers can pass it as a focusOverride.
   */
  function moveFocusToNearest(direction: 'left' | 'right'): string | null {
    const selected = getSelectedKeys();
    if (selected.size === 0) {
      return null;
    }

    // Find the boundary index (leftmost for 'left', rightmost for 'right').
    let boundaryIndex = direction === 'left' ? allKeys.length : -1;
    for (const key of selected) {
      const i = allKeys.indexOf(key);
      if (i === -1) {
        continue;
      }
      if (direction === 'left' && i < boundaryIndex) {
        boundaryIndex = i;
      }
      if (direction === 'right' && i > boundaryIndex) {
        boundaryIndex = i;
      }
    }

    // Walk outward from the boundary to find the nearest FREE_TEXT key.
    const step = direction === 'left' ? -1 : 1;
    for (let i = boundaryIndex + step; direction === 'left' ? i >= 0 : i < allKeys.length; i += step) {
      if (isFreeTextKey(allKeys[i])) {
        const targetKey = allKeys[i];
        state.setFocusedKey(targetKey);
        // Move DOM focus to the FreeText input inside the target container.
        const containerEl = gridRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(targetKey)}"]`);
        const focusTarget = containerEl?.querySelector<HTMLElement>('input:not([data-part])') ?? containerEl;
        focusTarget?.focus();
        return targetKey;
      }
    }
    return null;
  }

  return (
    <input
      ref={inputRef}
      aria-label={msg('queryBuilder.filterBar.selectionHandlerLabel')}
      readOnly
      tabIndex={-1}
      className={styles.visuallyHidden}
      onKeyDown={(e) => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          const tokens = getSelectedParsedTokens(getSelectedKeys(), flatTokens);
          const targetKey = moveFocusToNearest('left');
          clearSelection();
          dispatch({
            type: 'REPLACE_SELECTION',
            tokens,
            replacement: '',
            ...(targetKey ? { focusOverride: { itemKey: targetKey } } : {}),
          });
        } else if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          e.preventDefault();
          selectInDirection(e.key === 'ArrowLeft' ? 'left' : 'right');
        } else if (
          !e.shiftKey &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.altKey &&
          (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
        ) {
          // A caret-jump modifier (Cmd/Ctrl/Alt+Arrow) should perform a native caret
          // jump, not collapse the selection — bail out of this branch when one is held.
          e.preventDefault();
          moveFocusToNearest(e.key === 'ArrowLeft' ? 'left' : 'right');
          clearSelection();
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
          e.preventDefault();
          state.selectAll(allKeys);
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
          e.preventDefault();
          const text = formatClipboard(buildCopyText(getSelectedKeys(), allKeys, flatTokens, gridRef));
          navigator.clipboard.writeText(text).catch(() => {});
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
          e.preventDefault();
          const text = formatClipboard(buildCopyText(getSelectedKeys(), allKeys, flatTokens, gridRef));
          const tokens = getSelectedParsedTokens(getSelectedKeys(), flatTokens);
          navigator.clipboard
            .writeText(text)
            .then(() => {
              const targetKey = moveFocusToNearest('left');
              clearSelection();
              dispatch({
                type: 'REPLACE_SELECTION',
                tokens,
                replacement: '',
                ...(targetKey ? { focusOverride: { itemKey: targetKey } } : {}),
              });
            })
            .catch(() => {});
        }
        // Undo/redo (Cmd/Ctrl+Z, Cmd/Ctrl+Shift+Z, Ctrl+Y) are intentionally not handled
        // here: this hidden input is a sibling of the grid, so the event could never reach
        // grid-level handlers. FilterBar now owns those shortcuts on the outer container
        // (an ancestor of this input) and clears the selection itself, so we let the
        // keystroke propagate untouched.
        else if (e.key === '(' || e.key === ')') {
          e.preventDefault();
          const tokens = getSelectedParsedTokens(getSelectedKeys(), flatTokens);
          clearSelection();
          dispatch({ type: 'WRAP_SELECTION', tokens });
        } else if (isPrintableKey(e)) {
          e.preventDefault();
          const tokens = getSelectedParsedTokens(getSelectedKeys(), flatTokens);
          clearSelection();
          dispatch({ type: 'REPLACE_SELECTION', tokens, replacement: e.key });
        }
      }}
      onPaste={(e) => {
        const raw = e.clipboardData.getData('text/plain').trim();
        if (!raw) {
          return;
        }
        e.preventDefault();
        const tokens = getSelectedParsedTokens(getSelectedKeys(), flatTokens);
        const targetKey = moveFocusToNearest('left');
        clearSelection();
        dispatch({
          type: 'REPLACE_SELECTION',
          tokens,
          replacement: parseClipboard(raw),
          ...(targetKey ? { focusOverride: { itemKey: targetKey } } : {}),
        });
      }}
      onBlur={(e) => {
        // Keep selection alive when React Aria transiently moves focus to the grid
        // container itself during re-renders; clear in all other cases.
        if (e.relatedTarget !== gridRef.current) {
          clearSelection();
        }
      }}
    />
  );
}

SelectionKeyHandler.displayName = 'SelectionKeyHandler';

/**
 * Build a copy-paste string by walking allKeys in document order.
 *
 * - "ft-" boundary keys: reads the live DOM input value so uncommitted text
 *   typed in a FreeTextToken slot is included in the copy.
 * - Numeric content keys: emits the flat token's .text; PAREN open/close pairs
 *   that reference the same EXPRESSION are emitted once via a seen-set, and a
 *   high-watermark guard prevents inner tokens from duplicating their parent's text.
 */
function buildCopyText(
  selectedKeys: Set<string>,
  allKeys: string[],
  flatTokens: FlattenedToken[],
  gridRef: React.RefObject<HTMLElement | null>,
): string {
  const parts: string[] = [];
  let highWater = -1;
  const seenExprs = new Set<Extract<ParsedToken, { type: 'EXPRESSION' }>>();

  for (const key of allKeys) {
    if (!selectedKeys.has(key)) {
      continue;
    }

    if (key.startsWith('ft-')) {
      // Auto-inserted boundary: read live input value which may be uncommitted.
      const el = gridRef.current?.querySelector<HTMLInputElement>(
        `[data-freetext-token][data-key="${CSS.escape(key)}"] input`,
      );
      if (el?.value) {
        parts.push(el.value);
      }
    } else {
      const i = parseInt(key, 10);
      if (isNaN(i)) {
        continue;
      }
      const flatToken = flatTokens[i];
      if (!flatToken) {
        continue;
      }

      if (flatToken.type === 'PAREN') {
        // Both open and close PAREN reference the same EXPRESSION — emit it once.
        if (seenExprs.has(flatToken.expression)) {
          continue;
        }
        seenExprs.add(flatToken.expression);
        const { start, end } = flatToken.expression.location;
        if (start.offset >= highWater) {
          parts.push(flatToken.expression.text);
          highWater = end.offset;
        }
      } else {
        const { start, end } = flatToken.location;
        if (start.offset >= highWater) {
          parts.push(flatToken.text);
          highWater = end.offset;
        }
      }
    }
  }

  return parts.join(' ');
}

function isPrintableKey(e: React.KeyboardEvent): boolean {
  return !e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1;
}
